/**
 * Lista de Pedidos — /pedidos (100% fiel ao mockup Fase 2 "Lista Pedidos").
 *
 * Estrutura:
 *   header  → breadcrumb + título + sub "{N} ativos · R$ {valor} em produção"
 *             + Exportar + Novo pedido
 *   cards   → 5 cards .cmp (Recebidos/Em produção●/Em expedição/Entregues 30d/
 *             Cancelados); clicar = filtro; ativo = borda+número accent;
 *             Cancelados = número danger
 *   .table-wrap (card) →
 *     filtros → busca + Filtros[n] + segmentado Todos/Meus/Equipe +
 *               "N RESULTADOS · M visíveis" + "25 por página ▾"
 *     tabela  → ☑ Código · Cliente(avatar+email) · Vendedor · Status ·
 *               Entrega · Margem(colorida) · Valor · ⋯
 *     paginação → "Mostrando X–Y de N" + ← Anterior · páginas · Próxima →
 *   bulkbar → Exportar / Marcar entregue / Atribuir vendedor / Cancelar
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiDownload, FiFilter, FiMoreVertical, FiPlus, FiTruck, FiUserCheck, FiXCircle,
} from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  atribuirVendedorLote, cancelarLote, contagemStatusPedidos,
  listarPedidos, marcarEntregueLote,
} from "../../services/pedidos";
import { PageContainer } from "../../styles/shared";

import { fmtMoeda, SITUACAO_TONE_PEDIDO } from "../Orcamentos/helpers";
import { getItemTenant, setItemTenant } from "../../utils/tenantStorage";

// MT-4.7: filtros persistidos por tenant (getItemTenant migra a chave antiga).
const STORAGE = "dash-erp:pedidos:filtros";

const STATUS_CARDS = [
  { conta: "RECEBIDO", filtro: "RECEBIDO", label: "Recebidos" },
  { conta: "EM_PRODUCAO", filtro: "EM_PRODUCAO", label: "Em produção", dot: true },
  { conta: "EM_EXPEDICAO", filtro: "EM_EXPEDICAO", label: "Em expedição" },
  { conta: "ENTREGUE_30D", filtro: "ENTREGUE", label: "Entregues (30d)" },
  { conta: "CANCELADO", filtro: "CANCELADO", label: "Cancelados", danger: true },
];

const SEGMENTOS = [["todos", "Todos"], ["meus", "Meus"], ["equipe", "Equipe"]];
const PAGE_SIZES = [25, 50, 100];
const PRIORIDADES = [["URGENTE", "Urgente"], ["EXPRESS", "Express"], ["NORMAL", "Normal"], ["CORTESIA", "Cortesia"]];

function iniciais(nome) {
  const p = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (!p.length) return "—";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function fmtCompacto(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (n >= 1_000) return `R$ ${Math.round(n / 1_000)}k`;
  return `R$ ${Math.round(n)}`;
}

function fmtDataCurta(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return null; }
}

function relativoEntrega(iso) {
  if (!iso) return null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const dias = Math.round((d - hoje) / 86400000);
  if (Number.isNaN(dias)) return null;
  if (dias === 0) return "hoje";
  if (dias === 1) return "amanhã";
  if (dias === -1) return "ontem";
  if (dias > 0) return `em ${dias}d`;
  return `há ${Math.abs(dias)}d`;
}

function corMargem(m) {
  if (m == null) return "var(--ink-3)";
  if (m >= 25) return "var(--success-text)";
  if (m >= 12) return "var(--warning-text)";
  return "var(--danger-text)";
}

function lerFiltros(sp) {
  const qs = {
    busca: sp.get("busca") || "",
    situacao: sp.get("situacao") || "",
    seg: sp.get("seg") || "todos",
    prioridade: sp.get("prioridade") || "",
  };
  if (qs.busca || qs.situacao || qs.seg !== "todos" || qs.prioridade) return qs;
  try {
    return { busca: "", situacao: "", seg: "todos", prioridade: "", ...JSON.parse(getItemTenant(STORAGE) || "{}") };
  } catch { return { busca: "", situacao: "", seg: "todos", prioridade: "" }; }
}

export default function PedidosLista() {
  const navigate = useNavigate();
  const { request, usuario, hasAllPermissions, isMaster } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filtros, setFiltros] = useState(() => lerFiltros(searchParams));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [estado, setEstado] = useState({ loading: true, error: null, pedidos: [], total: 0 });
  const [contagem, setContagem] = useState(null);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelar, setMotivoCancelar] = useState("");
  const [atribuindo, setAtribuindo] = useState(false);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorEscolhido, setVendedorEscolhido] = useState("");
  const [filtrosAberto, setFiltrosAberto] = useState(false);
  const buscaTimer = useRef(null);

  const podeEditar = isMaster || hasAllPermissions(["pedido:edit"]);
  const podeCancelar = isMaster || hasAllPermissions(["pedido:cancel"]);
  const podeCriarOrcamento = isMaster || hasAllPermissions(["orcamento:create"]);

  const vendedorFiltro = filtros.seg === "meus" ? (usuario?.id || usuario?.usuario_id) : undefined;
  const filtrosAvancados = [filtros.prioridade].filter(Boolean).length;

  const carregar = useCallback(async () => {
    setEstado((p) => ({ ...p, loading: true, error: null }));
    setSelecionados(new Set());
    try {
      const [payload, cont] = await Promise.all([
        listarPedidos(request, {
          busca: filtros.busca || undefined,
          situacao: filtros.situacao || undefined,
          prioridade: filtros.prioridade || undefined,
          vendedor: vendedorFiltro || undefined,
          page, pageSize,
        }),
        contagemStatusPedidos(request, { vendedor: vendedorFiltro || undefined }).catch(() => null),
      ]);
      const items = payload?.results || payload || [];
      const total = typeof payload?.count === "number" ? payload.count : (Array.isArray(items) ? items.length : 0);
      setEstado({ loading: false, error: null, pedidos: Array.isArray(items) ? items : [], total });
      if (cont) setContagem(cont);
    } catch (err) {
      setEstado({ loading: false, error: err?.message || "Erro ao carregar pedidos.", pedidos: [], total: 0 });
    }
  }, [request, filtros.busca, filtros.situacao, filtros.prioridade, vendedorFiltro, page, pageSize]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    setItemTenant(STORAGE, JSON.stringify(filtros));
  }, [filtros]);

  const aplicar = (patch) => {
    const novo = { ...filtros, ...patch };
    setFiltros(novo);
    setPage(1);
    const usp = new URLSearchParams();
    if (novo.busca) usp.set("busca", novo.busca);
    if (novo.situacao) usp.set("situacao", novo.situacao);
    if (novo.seg && novo.seg !== "todos") usp.set("seg", novo.seg);
    if (novo.prioridade) usp.set("prioridade", novo.prioridade);
    setSearchParams(usp, { replace: true });
  };

  const onBusca = (valor) => {
    setFiltros((f) => ({ ...f, busca: valor }));
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    buscaTimer.current = setTimeout(() => aplicar({ busca: valor }), 300);
  };

  const toggleStatus = (filtro) => aplicar({ situacao: filtros.situacao === filtro ? "" : filtro });

  const pedidos = estado.pedidos;
  const subtitulo = useMemo(() => {
    if (!contagem) return estado.loading ? "Carregando…" : `${estado.total} pedidos`;
    return `${contagem.ativos} ativos · ${fmtCompacto(contagem.valor_em_producao)} em produção`;
  }, [contagem, estado.total, estado.loading]);

  // Seleção
  const allChecked = pedidos.length > 0 && selecionados.size === pedidos.length;
  const toggleTodos = () => setSelecionados(allChecked ? new Set() : new Set(pedidos.map((p) => p.id)));
  const toggleUm = (id) => setSelecionados((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const idsSelecionados = useMemo(() => [...selecionados], [selecionados]);

  // Paginação
  const totalPaginas = Math.max(1, Math.ceil(estado.total / pageSize));
  const inicio = estado.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const fim = Math.min(page * pageSize, estado.total);
  const paginasVisiveis = useMemo(() => {
    const set = new Set([1, totalPaginas, page, page - 1, page + 1]);
    return [...set].filter((n) => n >= 1 && n <= totalPaginas).sort((a, b) => a - b);
  }, [page, totalPaginas]);

  // Exportar CSV
  const exportarCsv = (apenasSelecionados) => {
    const base = apenasSelecionados ? pedidos.filter((p) => selecionados.has(p.id)) : pedidos;
    const head = ["Código", "Cliente", "Email", "Vendedor", "Status", "Entrega prevista", "Margem", "Valor"];
    const linhas = base.map((p) => [
      p.numero, p.cliente_nome || "", p.cliente_email || "", p.vendedor_nome || "",
      p.situacao_display, fmtDataCurta(p.data_entrega_prevista) || "",
      p.margem != null ? `${Math.round(p.margem)}%` : "", p.valor_total,
    ]);
    const csv = [head, ...linhas].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "pedidos.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Ações em lote
  const aposLote = async (res, verbo) => {
    const ok = res?.ok?.length || 0, erros = res?.erros?.length || 0;
    if (ok) toast.success(`${ok} pedido(s) ${verbo}.`);
    if (erros) toast.error(`${erros} pedido(s) não puderam ser ${verbo}.`);
    await carregar();
  };
  const marcarEntregue = async () => aposLote(await marcarEntregueLote(request, idsSelecionados), "marcados como entregues");
  const confirmarCancelar = async () => {
    if (!motivoCancelar.trim()) return;
    const res = await cancelarLote(request, idsSelecionados, motivoCancelar.trim());
    setCancelando(false); setMotivoCancelar(""); await aposLote(res, "cancelados");
  };
  const abrirAtribuir = async () => {
    setAtribuindo(true);
    if (!vendedores.length) {
      try {
        const r = await request("/api/usuarios?por_pagina=100");
        const lista = r?.data || r?.results || (Array.isArray(r) ? r : []);
        setVendedores(lista.map((u) => ({ id: u.id, nome: u.nome || u.email })));
      } catch { /* auto-toast */ }
    }
  };
  const confirmarAtribuir = async () => {
    const res = await atribuirVendedorLote(request, idsSelecionados, vendedorEscolhido || null);
    setAtribuindo(false); setVendedorEscolhido(""); await aposLote(res, "atualizados");
  };

  return (
    <PageContainer style={{ maxWidth: "none" }}>
      {/* Header */}
      <div className="flex between" style={{ alignItems: "flex-end", gap: 16, marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div className="breadcrumb">
            <span className="crumb">Início</span><span className="crumb-sep">/</span>
            <span className="crumb">Comercial</span><span className="crumb-sep">/</span>
            <span className="crumb active">Pedidos</span>
          </div>
          <h1 className="page-title">Pedidos</h1>
          <div className="page-sub">{subtitulo}</div>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn btn--ghost" onClick={() => exportarCsv(false)} disabled={!pedidos.length}>
            <FiDownload aria-hidden="true" /> Exportar
          </button>
          {podeCriarOrcamento && (
            <button type="button" className="btn btn--primary" onClick={() => navigate("/crm")}>
              <FiPlus aria-hidden="true" /> Novo pedido
            </button>
          )}
        </div>
      </div>

      {/* Cards de status */}
      <div className="cmp-grid" style={{ gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 14 }}>
        {STATUS_CARDS.map((c) => {
          const ativo = filtros.situacao === c.filtro;
          const n = contagem ? contagem[c.conta] ?? 0 : "—";
          const cor = ativo ? "var(--accent)" : c.danger ? "var(--danger-text)" : "var(--ink)";
          return (
            <button
              key={c.conta}
              type="button"
              className="cmp"
              onClick={() => toggleStatus(c.filtro)}
              style={{
                textAlign: "left", cursor: "pointer", padding: "16px 18px",
                borderColor: ativo ? "var(--accent)" : "var(--border)",
                background: ativo ? "var(--accent-soft)" : "var(--surface)",
                display: "flex", flexDirection: "column", gap: 8,
              }}
            >
              <span className={`tiny mono${ativo ? "" : " muted"}`} style={{ display: "flex", alignItems: "center", gap: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {c.label}
                {c.dot && <span style={{ width: 7, height: 7, borderRadius: 999, background: "var(--info-text)", display: "inline-block" }} />}
              </span>
              <strong style={{ fontSize: 22, fontFamily: "var(--font-display)", fontWeight: 600, color: cor }}>{n}</strong>
            </button>
          );
        })}
      </div>

      {/* Card tabela */}
      <div className="table-wrap">
        {/* Filtros */}
        <div className="flex between" style={{ gap: 12, padding: "12px 14px", flexWrap: "wrap", borderBottom: "1px solid var(--divider)" }}>
          <div className="flex gap-2" style={{ alignItems: "center", flexWrap: "wrap", flex: 1, minWidth: 0 }}>
            <input
              className="input" type="search" style={{ maxWidth: 300, minWidth: 180 }}
              placeholder="Buscar por código, cliente…"
              defaultValue={filtros.busca}
              onChange={(e) => onBusca(e.target.value)}
            />
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setFiltrosAberto(true)}>
              <FiFilter aria-hidden="true" /> Filtros
              {filtrosAvancados > 0 && <span className="badge accent no-dot" style={{ marginLeft: 6 }}>{filtrosAvancados}</span>}
            </button>
            <div className="seg three">
              {SEGMENTOS.map(([v, l]) => (
                <button key={v} type="button" className={filtros.seg === v ? "is-on" : ""} onClick={() => aplicar({ seg: v })}>{l}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2" style={{ alignItems: "center" }}>
            <span className="tiny muted" style={{ whiteSpace: "nowrap" }}>
              {estado.total} resultados · {pedidos.length} visíveis
            </span>
            <select
              className="input" style={{ width: "auto", height: 30, padding: "2px 8px", fontSize: 12 }}
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} por página</option>)}
            </select>
          </div>
        </div>

        {estado.error && <div className="so-banner danger" role="alert" style={{ margin: 12 }}>{estado.error}</div>}

        {estado.loading ? (
          <div style={{ padding: 28, color: "var(--ink-3)" }}>Carregando…</div>
        ) : pedidos.length === 0 ? (
          <div style={{ padding: 28, color: "var(--ink-3)" }}>
            {filtros.busca || filtros.situacao || filtros.prioridade || filtros.seg !== "todos"
              ? "Nenhum pedido com esses filtros."
              : "Nenhum pedido ainda. Crie um orçamento, aprove e use 'Converter em pedido'."}
          </div>
        ) : (
          <table className="dtable">
            <colgroup>
              <col style={{ width: "36px" }} /><col style={{ width: "13%" }} /><col style={{ width: "24%" }} />
              <col style={{ width: "14%" }} /><col style={{ width: "12%" }} /><col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} /><col style={{ width: "12%" }} /><col style={{ width: "44px" }} />
            </colgroup>
            <thead>
              <tr>
                <th><input type="checkbox" checked={allChecked} onChange={toggleTodos} aria-label="Selecionar todos" /></th>
                <th>Código</th><th>Cliente</th><th>Vendedor</th><th>Status</th><th>Entrega</th>
                <th className="num">Margem</th><th className="num">Valor</th><th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => {
                const rel = relativoEntrega(p.data_entrega_prevista);
                return (
                  <tr key={p.id} onClick={() => navigate(`/pedidos/${p.id}`)} style={{ cursor: "pointer" }} title="Abrir pedido">
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selecionados.has(p.id)} onChange={() => toggleUm(p.id)} aria-label={`Selecionar ${p.numero}`} />
                    </td>
                    <td>
                      <strong className="mono">{p.numero}</strong>
                      {p.numero_revisao > 0 && <span className="tiny muted"> .r{p.numero_revisao}</span>}
                    </td>
                    <td>
                      <div className="entity-row">
                        <div className="avatar-sm">{iniciais(p.cliente_nome)}</div>
                        <div className="entity-meta">
                          <div className="name">{p.cliente_nome || "—"}</div>
                          <div className="sub mono">{p.cliente_email || (p.revisao_em_andamento ? "em revisão" : "—")}</div>
                        </div>
                      </div>
                    </td>
                    <td>{p.vendedor_nome || <span className="muted">—</span>}</td>
                    <td><span className={`badge ${SITUACAO_TONE_PEDIDO[p.situacao] || "neutral"}`}>{p.situacao_display}</span></td>
                    <td>
                      {p.data_entrega_prevista ? (
                        <>
                          <span className="mono">{fmtDataCurta(p.data_entrega_prevista)}</span>
                          {rel && <div className="tiny muted">{rel}</div>}
                        </>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="num" style={{ color: corMargem(p.margem), fontWeight: 600 }}>
                      {p.margem != null ? `${p.margem.toFixed(1)}%` : "—"}
                    </td>
                    <td className="num">{fmtMoeda(p.valor_total)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="icon-btn" title="Abrir pedido" aria-label="Abrir pedido" onClick={() => navigate(`/pedidos/${p.id}`)}>
                        <FiMoreVertical size={15} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {!estado.loading && estado.total > 0 && (
          <div className="flex between" style={{ gap: 12, padding: "12px 14px", flexWrap: "wrap", borderTop: "1px solid var(--divider)" }}>
            <span className="tiny muted">Mostrando {inicio}–{fim} de {estado.total}</span>
            <div className="flex gap-2" style={{ alignItems: "center" }}>
              <button type="button" className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Anterior</button>
              {paginasVisiveis.map((n, i) => {
                const gap = i > 0 && n - paginasVisiveis[i - 1] > 1;
                return (
                  <React.Fragment key={n}>
                    {gap && <span className="tiny muted">…</span>}
                    <button
                      type="button"
                      className={`btn btn--sm ${n === page ? "btn--primary" : "btn--ghost"}`}
                      onClick={() => setPage(n)}
                    >{n}</button>
                  </React.Fragment>
                );
              })}
              <button type="button" className="btn btn--ghost btn--sm" disabled={page >= totalPaginas} onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}>Próxima →</button>
            </div>
          </div>
        )}
      </div>

      {/* Bulkbar */}
      {selecionados.size > 0 && (
        <div className="bulkbar" style={{
          position: "sticky", bottom: 12, marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          padding: "10px 16px", borderRadius: "var(--radius-lg)", background: "var(--surface)",
          border: "1px solid var(--border)", boxShadow: "0 8px 24px rgba(0,0,0,0.16)", zIndex: 6,
        }}>
          <strong style={{ fontSize: 13 }}>{selecionados.size} selecionado(s)</strong>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => exportarCsv(true)}><FiDownload aria-hidden="true" /> Exportar</button>
          {podeEditar && (
            <AsyncButton variant="secondary" onClick={marcarEntregue} successMessage="" errorMessage="Falha ao marcar entregues.">
              <FiTruck aria-hidden="true" /> Marcar como entregue
            </AsyncButton>
          )}
          {podeEditar && (
            <button type="button" className="btn btn--secondary btn--sm" onClick={abrirAtribuir}><FiUserCheck aria-hidden="true" /> Atribuir vendedor</button>
          )}
          {podeCancelar && (
            <button type="button" className="btn btn--danger btn--sm" onClick={() => setCancelando(true)}><FiXCircle aria-hidden="true" /> Cancelar</button>
          )}
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelecionados(new Set())} style={{ marginLeft: "auto" }}>Limpar seleção</button>
        </div>
      )}

      {/* Filtros avançados */}
      <SlideOverPanel
        open={filtrosAberto} onClose={() => setFiltrosAberto(false)}
        title="Filtros" subtitle="Refine a lista de pedidos" width="min(90vw, 420px)"
        footer={
          <div className="flex gap-2" style={{ justifyContent: "space-between", width: "100%" }}>
            <button type="button" className="btn btn--ghost" onClick={() => { aplicar({ prioridade: "" }); setFiltrosAberto(false); }}>Limpar</button>
            <button type="button" className="btn btn--primary" onClick={() => setFiltrosAberto(false)}>Fechar</button>
          </div>
        }
      >
        <div className="input-group">
          <label className="input-label">Prioridade</label>
          <select className="input" value={filtros.prioridade} onChange={(e) => aplicar({ prioridade: e.target.value })}>
            <option value="">Todas</option>
            {PRIORIDADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </SlideOverPanel>

      {/* Cancelar em lote */}
      <SlideOverPanel
        open={cancelando} onClose={() => setCancelando(false)}
        title="Cancelar pedidos em lote" subtitle={`${selecionados.size} pedido(s) — ação irreversível`} width="min(90vw, 460px)"
        footer={
          <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn btn--secondary" onClick={() => setCancelando(false)}>Voltar</button>
            <AsyncButton variant="danger" onClick={confirmarCancelar} silentSuccess>Cancelar pedidos</AsyncButton>
          </div>
        }
      >
        <div className="input-group">
          <label className="input-label">Motivo do cancelamento *</label>
          <textarea className="input" rows={4} value={motivoCancelar} onChange={(e) => setMotivoCancelar(e.target.value)} autoFocus placeholder="Ex.: cliente desistiu da compra." />
        </div>
      </SlideOverPanel>

      {/* Atribuir vendedor */}
      <SlideOverPanel
        open={atribuindo} onClose={() => setAtribuindo(false)}
        title="Atribuir vendedor" subtitle={`${selecionados.size} pedido(s) selecionado(s)`} width="min(90vw, 460px)"
        footer={
          <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn btn--secondary" onClick={() => setAtribuindo(false)}>Voltar</button>
            <AsyncButton variant="primary" onClick={confirmarAtribuir} silentSuccess>Aplicar</AsyncButton>
          </div>
        }
      >
        <div className="input-group">
          <label className="input-label">Vendedor</label>
          <select className="input" value={vendedorEscolhido} onChange={(e) => setVendedorEscolhido(e.target.value)}>
            <option value="">— Remover vendedor —</option>
            {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nome}</option>)}
          </select>
        </div>
      </SlideOverPanel>
    </PageContainer>
  );
}
